import { computeFlexColumn, placedContainerTop, type ChildBox } from 'Shared/posters/canvas/container/ContainerImpl'
import type { RuntimeObject, RuntimeContainer, Position, Dimensions, Box } from 'Shared/posters/types'
import type { ContainerLayout } from 'Shared/posters/project/posterSchema'

// ── Pure design-layout engine ───────────────────────────────────────────────────
//
// The user-mode flat view is *linked* to the original nested template design: the template stays
// the layout authority so the flat leaves keep reflowing when the record changes (a field gaining a
// line, an act hidden, a font tweak). This module reproduces the container flex-layout that
// ContainerImpl runs in the DOM, but as a pure function driven by an injected per-leaf size map —
// so the template can lay its leaves out without being mounted/rendered itself. Only text leaves
// need a real (hidden) mount to be measured; that measurement arrives here through `metricsOf`.
//
// The walk mirrors flatten.ts:walk (depth-first, absolute offset = parent offset + local position),
// but positions come from computeFlexColumn (the same code ContainerImpl uses) instead of reading a
// live getBBox. The result is every placed object's absolute box, keyed by id — the values the
// write-back reconciles into the flat store.

const FLOOR_H = 20

// A leaf's measured geometry, supplied by the caller (flat leaves / hidden text-measure layer).
// coy is the text content offset (glyphs render above the box origin — a negative value); 0 for
// images. Returning undefined omits the leaf from layout entirely — the caller's signal that a
// field resolved empty and should take no slot/gap (mirrors ContainerImpl.visibleObjects()).
export interface LeafMetrics {
	size: Dimensions
	coy:  number
}

export type MetricsOf = (id: string) => LeafMetrics | undefined

interface Ctx {
	metricsOf: MetricsOf
	canvas:    Dimensions
	out:       Map<string, Box>
}

// An object's measured size. Containers derive width from stored size (falling back to the canvas)
// and height from their fitted content; leaves defer to metricsOf. Null means "omit from layout".
function objectSize(obj: RuntimeObject, ctx: Ctx): Dimensions | null
{
	if (obj.type === 'container') {
		const c = obj as RuntimeContainer
		return { width: c.size?.width ?? ctx.canvas.width, height: containerHeight(c, ctx) }
	}
	const m = ctx.metricsOf(obj.id)
	return m ? m.size : null
}

function objectCoy(obj: RuntimeObject, ctx: Ctx): number
{
	if (obj.type === 'container') return 0
	return ctx.metricsOf(obj.id)?.coy ?? 0
}

// The container's drawn height: its designed height acts as a minimum that the fitted content can
// grow past but never shrink below (see ContainerImpl.effectiveHeight).
function containerHeight(c: RuntimeContainer, ctx: Ctx): number
{
	const content = minContentHeight(c, ctx)
	const min     = c.size?.height ?? content
	return Math.max(min, content)
}

// The height the container's visible children demand, per its layout: absolute → the furthest child
// edge; a stacked column → the summed heights plus gaps; a distribution column → the summed heights
// (the free space is spacing, not height). Mirrors ContainerImpl.computeMinSize (height axis only).
function minContentHeight(c: RuntimeContainer, ctx: Ctx): number
{
	const mode  = c.layout ?? 'absolute'
	const items = visibleChildren(c, ctx)
	if (items.length === 0) return FLOOR_H

	if (mode === 'absolute') {
		let h = FLOOR_H
		for (const o of items) {
			const size = objectSize(o, ctx)!
			const top  = (o.position?.y ?? 0) + objectCoy(o, ctx)
			h = Math.max(h, top + size.height)
		}
		return h
	}

	const heights = items.map(o => objectSize(o, ctx)!.height)
	const totalH  = heights.reduce((sum, v) => sum + v, 0)
	const stacked = mode === 'top' || mode === 'center' || mode === 'bottom'
	const gaps    = stacked ? (c.gap ?? 10) * Math.max(0, items.length - 1) : 0
	return Math.max(FLOOR_H, totalH + gaps)
}

function visibleChildren(c: RuntimeContainer, ctx: Ctx): RuntimeObject[]
{
	return (c.objects ?? []).filter(o => objectSize(o, ctx) !== null)
}

// Place a set of siblings within a box of size `dims` whose top-left is at absolute `offset`, writing
// each placed object's absolute box into ctx.out and recursing into containers. `layout` etc. are the
// parent's layout params (the canvas root behaves as an absolute container).
function place(objs: RuntimeObject[], dims: Dimensions, offset: Position, layout: ContainerLayout, gap: number, align: 'left' | 'center' | 'right', ctx: Ctx): void
{
	const items = objs.filter(o => objectSize(o, ctx) !== null)
	if (items.length === 0) return

	let local: Record<string, Position>
	if (layout === 'absolute') {
		local = {}
		for (const o of items)
			local[o.id] = { x: o.position?.x ?? 0, y: o.position?.y ?? 0 }
	} else {
		const boxes: ChildBox[] = items.map(o => ({ id: o.id, dims: objectSize(o, ctx)!, coy: objectCoy(o, ctx) }))
		local = computeFlexColumn(layout, gap, align, dims.width, dims.height, boxes)
	}

	for (const o of items) {
		const size = objectSize(o, ctx)!
		let top = offset.y + local[o.id].y
		const left = offset.x + local[o.id].x

		if (o.type === 'container') {
			const c = o as RuntimeContainer
			// An absolutely-placed container that grew past its designed height re-anchors its top per
			// layout direction (top grows down, bottom grows up, else from the centre) and clamps into
			// the parent — the same grow-anchor ContainerImpl.effectiveBox applies. A flex parent already
			// positioned it by its grown height, so no re-anchor there.
			if (layout === 'absolute') {
				const storedH = c.size?.height ?? size.height
				if (size.height > storedH)
					top = offset.y + placedContainerTop(c.layout ?? 'absolute', dims.height, local[o.id].y, storedH, size.height)
			}
			const box: Box = { pos: { x: left, y: top }, dims: size }
			ctx.out.set(o.id, box)
			place(c.objects ?? [], size, box.pos, c.layout ?? 'absolute', c.gap ?? 10, c.align ?? 'left', ctx)
		} else {
			ctx.out.set(o.id, { pos: { x: left, y: top }, dims: size })
		}
	}
}

// Lay out the template and return every placed object's absolute box, keyed by id. Leaves for which
// metricsOf returns undefined (resolved-empty fields) are omitted, as are their descendants.
export function layoutDesign(objects: RuntimeObject[], metricsOf: MetricsOf, canvas: Dimensions): Map<string, Box>
{
	const ctx: Ctx = { metricsOf, canvas, out: new Map() }
	place(objects, canvas, { x: 0, y: 0 }, 'absolute', 0, 'left', ctx)
	return ctx.out
}
